import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.js";

interface Props {
  open: boolean;
  bookTitle: string;
  defaultPageCount: number | null;
  onConfirm: (canonicalPageCount: number | null, deadline: Date | null) => void;
  onCancel: () => void;
}

export function PromoteDialog({ open, bookTitle, defaultPageCount, onConfirm, onCancel }: Props) {
  const { t } = useTranslation();
  const [pageCountStr, setPageCountStr] = useState(defaultPageCount != null ? String(defaultPageCount) : "");
  const [deadlineStr, setDeadlineStr] = useState("");

  const handleConfirm = () => {
    const parsed = parseInt(pageCountStr, 10);
    const canonicalPageCount = !isNaN(parsed) && parsed > 0 ? parsed : null;
    const deadline = deadlineStr ? new Date(deadlineStr) : null;
    onConfirm(canonicalPageCount, deadline);
  };

  return (
    <ConfirmDialog
      open={open}
      title={t("club.books.startReading")}
      message={bookTitle}
      confirmLabel={t("club.books.startReading")}
      cancelLabel={t("common.cancel")}
      onConfirm={handleConfirm}
      onCancel={onCancel}
    >
      <div className="space-y-3">
        <div className="space-y-1.5">
          <label className="text-xs font-medium" style={{ color: "var(--color-muted-foreground)" }}>
            {t("club.books.pageCountLabel")}
          </label>
          <input
            type="number"
            min={1}
            className="w-full rounded-md px-3 py-1.5 text-sm transition-all focus:outline-none"
            style={{
              border: "1px solid var(--color-border)",
              background: "var(--color-background)",
              color: "var(--color-foreground)",
            }}
            placeholder={t("club.books.pageCountPlaceholder")}
            value={pageCountStr}
            onChange={(e) => setPageCountStr(e.target.value)}
            onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
            autoFocus
          />
          <p className="text-xs" style={{ color: "var(--color-muted-foreground)", opacity: 0.7 }}>
            {t("club.books.pageCountHint")}
          </p>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium" style={{ color: "var(--color-muted-foreground)" }}>
            {t("club.description.deadlineLabel")} <span style={{ opacity: 0.6 }}>{t("club.description.deadlineOptional")}</span>
          </label>
          <input
            type="date"
            className="w-full rounded-md px-3 py-1.5 text-sm transition-all focus:outline-none"
            style={{
              border: "1px solid var(--color-border)",
              background: "var(--color-background)",
              color: "var(--color-foreground)",
            }}
            value={deadlineStr}
            onChange={(e) => setDeadlineStr(e.target.value)}
            onFocus={(e) => (e.currentTarget.style.borderColor = "var(--color-accent)")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "var(--color-border)")}
          />
          <p className="text-xs" style={{ color: "var(--color-muted-foreground)", opacity: 0.7 }}>
            {t("club.description.deadlineSkipHint")}
          </p>
        </div>
      </div>
    </ConfirmDialog>
  );
}
